import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Home, ArrowRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { getDonationStatus } from "../lib/api";
import { LOGO_URL } from "../data/site";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

export default function DonateSuccess() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | error
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await getDonationStatus(sessionId);
        if (cancelled) return;
        setData(res);
        if (res.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("expired");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center" data-testid="donate-success-page">
      <div className="gk-container py-20">
        <div className="max-w-xl mx-auto bg-white rounded-[2rem] border border-black/5 shadow-xl p-10 text-center">
          <img src={LOGO_URL} alt="GKNL" className="h-24 w-24 mx-auto rounded-full bg-white border border-black/5 p-2" />

          {status === "checking" && (
            <div className="mt-6" data-testid="donate-status-checking">
              <Loader2 className="h-12 w-12 text-[#0B5D1E] animate-spin mx-auto" />
              <h1 className="mt-6 font-heading font-bold text-3xl">{t.common.checkingPayment}</h1>
              <p className="mt-3 text-neutral-600 text-sm">Session: {sessionId?.slice(0, 24)}…</p>
            </div>
          )}

          {status === "paid" && (
            <div className="mt-6" data-testid="donate-status-paid">
              <CheckCircle2 className="h-16 w-16 text-[#14803C] mx-auto" />
              <h1 className="mt-6 font-heading font-bold text-3xl text-[#0B5D1E]">
                {t.common.donationSuccess}
              </h1>
              {data?.amount_total && (
                <p className="mt-3 text-2xl font-heading font-bold text-neutral-900">
                  ${data.amount_total.toFixed(2)} {data.currency?.toUpperCase()}
                </p>
              )}
              <p className="mt-3 text-neutral-600">
                Your gift helps families, orphans, and communities across three continents. A receipt will be sent to your email.
              </p>
              <Link to="/" className="gk-btn-primary mt-8 inline-flex" data-testid="donate-success-home-btn">
                <Home size={18} />
                {t.common.backHome}
              </Link>
            </div>
          )}

          {(status === "expired" || status === "error") && (
            <div className="mt-6" data-testid="donate-status-error">
              <XCircle className="h-16 w-16 text-[#B5651D] mx-auto" />
              <h1 className="mt-6 font-heading font-bold text-3xl">
                {status === "expired" ? "Session expired" : "Could not confirm payment"}
              </h1>
              <p className="mt-3 text-neutral-600">
                If you believe this is in error, please contact us at grandkasainsangalubangu@gmail.com with your session ID.
              </p>
              <div className="mt-8 flex gap-3 justify-center">
                <Link to="/get-involved" className="gk-btn-secondary">
                  {t.common.tryAgain}
                  <ArrowRight size={16} />
                </Link>
                <Link to="/" className="gk-btn-outline">
                  <Home size={16} />
                  {t.common.backHome}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
